import React from 'react';
import { HeartPulse, TrendingUp } from 'lucide-react';

export const SustainabilityDashboard: React.FC<{ scorePct: number, completedItems: number, totalItems: number, getHealthGrade: (pct: number) => string }> = ({ scorePct, completedItems, totalItems, getHealthGrade }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Metric Ring Column */}
        <div className="bg-gradient-to-br from-slate-900 to-gray-900 text-white rounded-2xl p-6 shadow-xl border border-slate-800 flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Sustainability Score</span>
            <span className="bg-blue-500/20 text-blue-300 text-[10px] font-black uppercase px-2 py-0.5 rounded">
              A+ Blueprint Approved
            </span>
          </div>

          <div className="py-6 flex items-center gap-6">
            <div className="relative shrink-0 flex items-center justify-center">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle cx="48" cy="48" r="40" stroke="#1e293b" strokeWidth="8" fill="transparent" />
                <circle 
                  cx="48" 
                  cy="48" 
                  r="40" 
                  stroke="#3b82f6" 
                  strokeWidth="8" 
                  fill="transparent" 
                  strokeDasharray={251.2}
                  strokeDashoffset={251.2 - (251.2 * scorePct) / 100}
                  strokeLinecap="round"
                  className="transition-all duration-1000 ease-out"
                />
              </svg>
              <div className="absolute text-center">
                <span className="text-2xl font-black">{scorePct}%</span>
              </div>
            </div>

            <div>
              <div className="text-xs font-bold text-slate-400 uppercase tracking-tight">Active Grade</div>
              <div className="text-lg font-black text-blue-400">{getHealthGrade(scorePct)}</div>
              <p className="text-[11px] text-slate-400 mt-1">
                {completedItems} of {totalItems} structural guardrails validated.
              </p>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-4 mt-2">
            <div className="flex items-center gap-2 text-xs text-slate-300">
              <HeartPulse className="w-4 h-4 text-emerald-500 shrink-0" />
              <span>Ledger Code Integrity Status: **Pristine**</span>
            </div>
          </div>
        </div>

        {/* Proactive Operations Roadmap Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 lg:col-span-2 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 text-rose-600 mb-2">
              <TrendingUp className="w-5 h-5 shrink-0" />
              <h2 className="text-xs font-black uppercase tracking-widest">Dynamic Recommendations & Next Steps</h2>
            </div>
            <p className="text-xs text-gray-500 mb-4 leading-relaxed font-medium">
              We completed the single source of truth unification. Follow this proactive action roadmap to stay clean and secure.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3 bg-blue-50/60 rounded-xl border border-blue-100">
                <div className="font-bold text-blue-900 text-xs">
                  1. Deploy Sandbox Test Suite
                </div>
                <p className="text-[10px] mt-0.5 text-blue-750 leading-snug">
                  Execute automated CLI validations continuously to ensure changes are thoroughly dry-run before uploading to Firebase.
                </p>
              </div>
              
              <div className="p-3 bg-emerald-50/60 rounded-xl border border-emerald-100">
                <div className="font-bold text-emerald-900 text-xs">
                  2. Sync Firestore Rules
                </div>
                <p className="text-[10px] mt-0.5 text-emerald-750 leading-snug">
                  Deploy nested sub-collection rules to target only verified emails matching tenant affiliations.
                </p>
              </div>

              <div className="p-3 bg-indigo-50/60 rounded-xl border border-indigo-100">
                <div className="font-bold text-indigo-900 text-xs">
                  3. Align Backups to batchId
                </div>
                <p className="text-[10px] mt-0.5 text-indigo-750 leading-snug">
                  Periodically backup database configurations matching batch numbers for simple auditing and point-in-time rollbacks.
                </p>
              </div>

              <div className="p-3 bg-amber-50/60 rounded-xl border border-amber-100">
                <div className="font-bold text-amber-900 text-xs">
                  4. Dispute Resolution Audit
                </div>
                <p className="text-[10px] mt-0.5 text-amber-750 leading-snug">
                  Monitor that user disputes update reporting status values explicitly in cloud memory.
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
  );
};
